// SPDX-License-Identifier: AGPL-3.0-or-later

import {MessageReferenceTypes} from '@fluxer/constants/src/ChannelConstants';

export interface MentionShape {
	readonly mentionedUserIds: ReadonlyArray<string>;
	readonly mentionEveryone: boolean;
	readonly mentionRoleIds: ReadonlyArray<string>;
	readonly replyTargetAuthorId: string | null;
}

export interface MentionFacts extends MentionShape {
	readonly currentUserId: string | null;
	readonly authorId: string | null;
	readonly memberRoleIds: ReadonlySet<string> | null;
	readonly suppressEveryone: boolean;
	readonly suppressRoles: boolean;
}

export interface MentionSignals {
	readonly hasUserMention: boolean;
	readonly hasEveryoneMention: boolean;
	readonly hasRoleMention: boolean;
	readonly hasReplyToCurrentUser: boolean;
}

interface MentionMessageLike {
	readonly author?: {readonly id: string} | null;
	readonly mentions?: ReadonlyArray<{readonly id: string}> | null;
	readonly mention_everyone?: boolean | null;
	readonly mentionEveryone?: boolean | null;
	readonly mention_roles?: ReadonlyArray<string> | null;
	readonly mentionRoles?: ReadonlyArray<string> | null;
	readonly message_reference?: {readonly type?: number | null} | null;
	readonly messageReference?: {readonly type?: number | null} | null;
	readonly referenced_message?: {readonly author?: {readonly id: string} | null} | null;
	readonly referencedMessage?: {readonly author?: {readonly id: string} | null} | null;
}

const EMPTY: ReadonlyArray<never> = [];

export function readMentionShape(message: MentionMessageLike): MentionShape {
	const reference = message.messageReference ?? message.message_reference ?? null;
	const referenced = message.referencedMessage ?? message.referenced_message ?? null;
	const isReply =
		reference != null && (reference.type ?? MessageReferenceTypes.DEFAULT) === MessageReferenceTypes.DEFAULT;
	return {
		mentionedUserIds: message.mentions?.map((mention) => mention.id) ?? EMPTY,
		mentionEveryone: (message.mentionEveryone ?? message.mention_everyone) === true,
		mentionRoleIds: message.mentionRoles ?? message.mention_roles ?? EMPTY,
		replyTargetAuthorId: isReply ? (referenced?.author?.id ?? null) : null,
	};
}

export function resolveMentionSignals(facts: MentionFacts): MentionSignals {
	const {currentUserId} = facts;
	if (currentUserId == null) {
		return {hasUserMention: false, hasEveryoneMention: false, hasRoleMention: false, hasReplyToCurrentUser: false};
	}
	return {
		hasUserMention: facts.mentionedUserIds.includes(currentUserId),
		hasEveryoneMention: facts.mentionEveryone && !facts.suppressEveryone,
		hasRoleMention: !facts.suppressRoles && hasMatchingRole(facts.mentionRoleIds, facts.memberRoleIds),
		hasReplyToCurrentUser: facts.replyTargetAuthorId === currentUserId && facts.authorId !== currentUserId,
	};
}

export function hasExplicitMention(signals: MentionSignals): boolean {
	return signals.hasUserMention || signals.hasEveryoneMention || signals.hasRoleMention;
}

export function highlightsCurrentUser(signals: MentionSignals): boolean {
	return hasExplicitMention(signals) || signals.hasReplyToCurrentUser;
}

export function hasMatchingRole(
	mentionRoleIds: ReadonlyArray<string> | null | undefined,
	memberRoleIds: ReadonlySet<string> | null | undefined,
): boolean {
	if (memberRoleIds == null || mentionRoleIds == null || mentionRoleIds.length === 0) {
		return false;
	}
	for (const roleId of mentionRoleIds) {
		if (memberRoleIds.has(roleId)) {
			return true;
		}
	}
	return false;
}
